import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AppService } from 'src/@core/services/app.service';
import { JobService } from 'src/@core/services/job/job.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { entityTypes } from 'src/app/shared/helpers/roles-permissions';
import { PostCallSignal } from '../../entity-leads/models/post-call-signal.types';
import {
  extractPostCallSignals,
  signalStrengthClass,
} from '../../entity-leads/utils/post-call-signals.util';
import { SignalDetailPopupComponent } from '../signal-detail-popup/signal-detail-popup.component';

/** One answer row. `question` is display-only — the backend ignores edits to it. */
interface EditableAnswer {
  question: string;
  answer: string;
}

@Component({
  selector: 'hour4u-admin-call-summary-popup',
  templateUrl: './call-summary-popup.component.html',
  styleUrls: ['./call-summary-popup.component.scss']
})
export class CallSummaryPopupComponent implements OnInit {

  status: string;
  status_reason: string;
  call_transcript: string;
  client_name: string;
  questions_asked: any[] = [];
  call_sid: string;
  recordingUrl: string | null = null;
  job_id: string;
  project_id: string;
  people_id: string;

  /** Post-call signals (score / strength / reason / task_prompt). */
  signals: PostCallSignal[] = [];

  /** HOA staff only — the debug screen shows raw prompts across every client. */
  isInternal = false;

  // --- Human review -------------------------------------------------------
  // A reviewer corrects what the analysis wrote: the outcome, the reason, and
  // the answers. Everything is edited against a draft and only the draft is
  // sent, so closing without saving leaves the record exactly as it was.
  isEditing = false;
  isSaving = false;
  outcomeOptions: string[] = [];
  draftOutcome = '';
  draftReason = '';
  draftAnswers: EditableAnswer[] = [];
  aiOutcome = '';
  humanReviewedBy = '';
  humanReviewedAtLabel = '';
  private profile: any;

  // --- Add in other project -----------------------------------------------
  // The outcome pushed this lead into another project, but the push waits on a
  // human. Only records the backend sends as an explicit `false` are pending —
  // a missing field means the outcome never asked for the push at all.
  addInOtherProjectApproved: boolean | null = null;
  isApproving = false;

  constructor(
    public dialogRef: MatDialogRef<CallSummaryPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private router: Router,
    private appService: AppService,
    private jobService: JobService,
    private notify: NotificationService,
    private dialog: MatDialog,
  ) {
    this.status = data.status;
    this.status_reason = data.status_reason;
    this.call_transcript = data.call_transcript;
    this.client_name = data.client_name;
    this.questions_asked = data.questions_asked || [];
    this.call_sid = data.call_sid;
    this.recordingUrl = this.data.recording_url || null;
    this.job_id = data.job_id;
    this.project_id = data.project_id;
    this.people_id = data.people_id;
    this.signals = Array.isArray(data.signals) && data.signals.length
      ? data.signals
      : extractPostCallSignals(data);

    const account: any = this.appService.getCurrentAccount();
    const entityType = account && account.entity && account.entity.entityType;
    this.isInternal = entityType === entityTypes.INTERNAL;
  }

  ngOnInit() {
    this.profile = this.appService.getCurrentAccount();
    this.aiOutcome = (this.data && this.data.ai_outcome) || '';
    this.humanReviewedBy = (this.data && this.data.human_reviewed_by) || '';
    this.humanReviewedAtLabel = this.formatReviewedAt(this.data && this.data.human_reviewed_at);
    this.outcomeOptions = this.buildOutcomeOptions();
    const approved = this.data && this.data.add_in_other_project_approved;
    this.addInOtherProjectApproved = typeof approved === 'boolean' ? approved : null;
  }

  /** Pending approval — the backend marked this record `false`, not absent. */
  get canApproveAddInOtherProject(): boolean {
    return this.addInOtherProjectApproved === false && this.canEdit && !this.isEditing;
  }

  get showAddInOtherProjectStamp(): boolean {
    return this.addInOtherProjectApproved === true && !this.isEditing;
  }

  /** Saving needs the keys the endpoint resolves the DynamoDB row from. */
  get canEdit(): boolean {
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    return !!(entityId && this.project_id && this.people_id && this.job_id);
  }

  get hasReviewStamp(): boolean {
    return !!(this.humanReviewedBy || this.humanReviewedAtLabel);
  }

  startEditing(): void {
    if (!this.canEdit) {
      return;
    }
    this.draftOutcome = this.status || '';
    this.draftReason = this.status_reason || '';
    this.draftAnswers = (this.questions_asked || []).map((q: any) => ({
      question: (q && q.question) || '',
      answer: (q && q.answer) || ''
    }));
    this.isEditing = true;
  }

  cancelEditing(): void {
    this.isEditing = false;
    this.draftAnswers = [];
  }

  saveReview(): void {
    if (this.isSaving) {
      return;
    }
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    const outcome = (this.draftOutcome || '').trim();
    // An outcome is only mandatory when the call already had one. A project with
    // no configured outcomes must still be able to correct the reason/answers.
    if (!outcome && (this.status || '').trim()) {
      this.notify.showMessage('Please select an outcome', 3000);
      return;
    }

    // Only send what actually moved: an unchanged field left out of the body is
    // left untouched server-side, and never snapshots over the AI original.
    const outcomeChanged = !!outcome && outcome !== (this.status || '').trim();
    const reason = this.draftReason || '';
    const body: any = {
      projectId: this.project_id,
      peopleId: this.people_id,
      jobId: this.job_id,
      reviewedByHuman: true,
      outcome,
      disableExistingTask: outcomeChanged
    };
    if (reason !== (this.status_reason || '')) {
      body.statusReason = reason;
    }
    if (this.answersChanged()) {
      body.questionsAsked = this.draftAnswers.map(row => ({
        question: row.question,
        answer: row.answer
      }));
    }

    if (!outcomeChanged && !('statusReason' in body) && !('questionsAsked' in body)) {
      this.notify.showMessage('Nothing to save', 2000);
      this.cancelEditing();
      return;
    }

    this.isSaving = true;
    this.jobService.updateJobsStatusHumanReview(entityId, body).subscribe(
      (response: any) => {
        this.isSaving = false;
        this.applySavedResponse(response, outcome, reason);
        this.notify.showMessage('Call summary updated', 3000);
      },
      (err: any) => {
        this.isSaving = false;
        const msg =
          (err && err.error && err.error.error) ||
          (err && err.error && err.error.message) ||
          'Failed to update the call summary';
        this.notify.showMessage(msg, 5000);
      }
    );
  }

  /**
   * Show what the server stored, not what was typed: the backend keeps its own
   * question text and drops rows it does not recognise, so the draft is not
   * necessarily what landed.
   */
  private applySavedResponse(response: any, fallbackOutcome: string, fallbackReason: string): void {
    const saved = (response && response.data) || {};
    this.status = saved.outcome || fallbackOutcome;
    this.status_reason = saved.status_reason !== undefined ? saved.status_reason : fallbackReason;
    if (Array.isArray(saved.questions_asked) && saved.questions_asked.length) {
      this.questions_asked = saved.questions_asked;
    } else if (this.answersChanged()) {
      this.questions_asked = this.draftAnswers.map(row => ({ ...row }));
    }
    this.aiOutcome = saved.ai_outcome || this.aiOutcome;
    this.humanReviewedBy = saved.human_reviewed_by || this.humanReviewedBy;
    this.humanReviewedAtLabel =
      this.formatReviewedAt(saved.human_reviewed_at) || this.humanReviewedAtLabel;
    this.outcomeOptions = this.buildOutcomeOptions();
    this.isEditing = false;
    this.draftAnswers = [];
    // Tell connected-leads the row is stale so the list and counts refresh.
    this.data.saved = true;
  }

  private answersChanged(): boolean {
    const original = this.questions_asked || [];
    if (this.draftAnswers.length !== original.length) {
      return false;
    }
    return this.draftAnswers.some((row, index) => {
      const before = original[index];
      return row.answer !== ((before && before.answer) || '');
    });
  }

  /** The project's outcomes, plus the current one in case it is no longer offered. */
  private buildOutcomeOptions(): string[] {
    const fromProject = ((this.data && this.data.expectedOutcomes) || [])
      .map((o: any) => (o && o.outcome_name ? String(o.outcome_name).trim() : ''))
      .filter(Boolean);
    const current = (this.status || '').trim();
    if (current && !fromProject.includes(current)) {
      fromProject.unshift(current);
    }
    return fromProject;
  }

  private formatReviewedAt(raw: any): string {
    if (raw == null || raw === '') {
      return '';
    }
    const seconds = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (!Number.isFinite(seconds) || seconds <= 0) {
      return '';
    }
    const date = new Date(seconds < 1e12 ? seconds * 1000 : seconds);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  /**
   * Sign off the push into the other project. The button is replaced by a
   * stamp rather than staying clickable, and the row is marked stale so the
   * list behind the dialog refreshes with the new flag on close.
   */
  approveAddInOtherProject(): void {
    if (this.isApproving || !this.canApproveAddInOtherProject) {
      return;
    }
    const entityId = this.profile && this.profile.entity && this.profile.entity.id;
    this.isApproving = true;
    this.jobService.approveAddInOtherProject(entityId, {
      projectId: this.project_id,
      peopleId: this.people_id,
      jobId: this.job_id
    }).subscribe(
      () => {
        this.isApproving = false;
        this.addInOtherProjectApproved = true;
        this.data.saved = true;
        this.notify.showMessage('Approved. The lead will be added to the other project.', 3000);
      },
      (err: any) => {
        this.isApproving = false;
        const msg =
          (err && err.error && err.error.error) ||
          (err && err.error && err.error.message) ||
          'Failed to approve adding this lead to the other project';
        this.notify.showMessage(msg, 5000);
      }
    );
  }

  /** Open this call's analysis run — what the LLM was asked and what it answered. */
  openAnalysisDebug(): void {
    this.dialogRef.close(!!this.data.saved);
    this.router.navigate(['/call-analysis-debug/logs'], {
      queryParams: {
        job_id: this.job_id || undefined,
        call_sid: this.job_id ? undefined : this.call_sid,
        project_id: this.project_id || undefined
      }
    });
  }

  signalStrengthClass(strength: string | undefined | null): string {
    return signalStrengthClass(strength);
  }

  openSignalDetail(signal: PostCallSignal): void {
    this.dialog.open(SignalDetailPopupComponent, {
      width: '480px',
      maxHeight: '90vh',
      panelClass: 'add-edit-staff-dialog',
      disableClose: false,
      data: {
        signal,
        client_name: this.client_name,
      },
    });
  }

  ngOnDestroy() { }

  close(): void {
    this.dialogRef.close(!!this.data.saved);
  }
}
